import { EPSILON } from '../../../src/measurement/MeasureCommon';
import {
  detectCurvesAndLines,
  detectCircleFromLoop,
  detectEllipseFromLoop,
  getCentroid } from
'./SurfaceEdgeDetectionUtils';
import {
  MIN_CIRCLE_EDGES,
  MIN_CURVE_ANGLE as MIN_CURVE_INTERIOR_ANGLE,
  MAX_CURVE_ANGLE as MAX_CURVE_INTERIOR_ANGLE,
  ANGLE_PRECISION,
  SHAPE_TYPES,
  CREASE_ANGLE_RADIANS,
  WELDING_EPSILON,
  MAX_SURFACE_INDEX_BUFFER,
  MIN_SCALE_COMPONENT } from
'./MeasureConstants';

const av = Autodesk.Viewing;
const avp = av.Private;
const MeasureCommon = av.MeasureCommon;

// Per-geometry weld-map cache. Keyed by geometry object so the cache is GC'd with the
// geometry and we never mutate shared model data with extension-private fields.
const geometryWeldCache = new WeakMap();

function getCanonOf(geometry) {
  let canonOf = geometryWeldCache.get(geometry);
  if (!canonOf) {
    canonOf = buildWeldMap(geometry);
    geometryWeldCache.set(geometry, canonOf);
  }
  return canonOf;
}

/**
 * Computes the surface measurement result from picks
 * @param {Object} picks - The measurement picks
 * @param {Object} viewer - The viewer instance
 * @returns {Object|null} The surface measurement result
 */
export function computeSurfaceResult(picks, viewer) {
  const firstPick = picks[1];
  const faceNormal = firstPick?.faceNormal;
  if (!firstPick || !faceNormal) {
    return null;
  }

  const surfaceData = extractSurfaceData(firstPick, viewer);
  const outerLoop = surfaceData?.vertices?.[0];
  if (!outerLoop) {
    return null;
  }

  return {
    intersectPoint: firstPick.intersectPoint,
    surfaceData,
    surfaceProperties: {
      area: surfaceData.area,
      centroid: getCentroid(outerLoop),
      vertexCount: outerLoop.length
    },
    type: MeasureCommon.MeasurementTypes.MEASUREMENT_SURFACE
  };
}

/**
 * Creates a canonical edge key from two vertex indices using the weld map.
 * Ensures consistent edge identification regardless of vertex order.
 * @param {number[]} canonOf - The canonical vertex index map
 * @param {number} vA - First vertex index
 * @param {number} vB - Second vertex index
 * @returns {string} A unique key for the edge
 */
function makeEdgeKey(canonOf, vA, vB) {
  const canonA = canonOf[vA];
  const canonB = canonOf[vB];
  return canonA < canonB ? `${canonA}_${canonB}` : `${canonB}_${canonA}`;
}

// 3D shoelace: |sum_i (p_i x p_{i+1})| / 2 — translation-invariant unsigned area of the
// planar polygon defined by `points`. Result is meaningless for non-planar loops, so callers
// must gate this on isSurfaceCurved === false.
function calculateSurfaceArea(points) {
  if (!points || points.length < 3) {
    return 0;
  }

  const vectorArea = new THREE.Vector3(0, 0, 0);
  const cross = new THREE.Vector3();
  const n = points.length;
  for (let i = 0; i < n; i++) {
    cross.crossVectors(points[i], points[(i + 1) % n]);
    vectorArea.add(cross);
  }
  return vectorArea.length() / 2;
}

function buildWeldMap(geometry, epsilon = WELDING_EPSILON) {
  const readPosition = avp.VertexEnumerator.makeReadPositionFunc(geometry);
  const numVertices = avp.VertexEnumerator.getVertexCount(geometry);

  const keyToCanon = new Map(); // quantized position -> canonical vertex id
  const canonOf = new Array(numVertices); //canonOf[originalIndex] = canonicalIndex
  const _v = { x: 0, y: 0, z: 0 };

  let canonCount = 0;
  for (let i = 0; i < numVertices; i++) {
    readPosition(i, _v);

    const key = `${Math.round(_v.x / epsilon)},${Math.round(_v.y / epsilon)},${Math.round(_v.z / epsilon)}`;

    let canon = keyToCanon.get(key);
    if (canon === undefined) {
      canon = canonCount++;
      keyToCanon.set(key, canon);
    }
    canonOf[i] = canon;
  }
  return canonOf;
}

function getEdgeMapWelded(geometry, canonOf) {
  const ib = geometry.ib;
  if (!ib) return null;

  const edgeMap = new Map();

  for (let i = 0; i < ib.length; i += 3) {
    const v0 = ib[i];
    const v1 = ib[i + 1];
    const v2 = ib[i + 2];

    const edges = [[v0, v1], [v1, v2], [v2, v0]];
    for (const [vA, vB] of edges) {
      const key = makeEdgeKey(canonOf, vA, vB);
      let arr = edgeMap.get(key);
      if (!arr) {
        edgeMap.set(key, arr = []);
      }
      arr.push(i);
    }
  }

  return edgeMap;
}

/**
 * Extracts oriented boundary loops from a set of boundary edges.
 *
 * Edge-based walker (mirrors elephant's `extractEdgeLoops` in
 * `ElephantVertexGlm/3D/Measurement/EdgeLoopUtils.cpp`):
 *   - Each edge is consumed at most once (`processedEdges`).
 *   - When the walk revisits a vertex seen earlier in the current loop, the
 *     repeated section is clipped out as a closed sub-loop — naturally handles
 *     self-intersections at non-manifold vertices.
 *   - Starts are biased to chain endpoints (degree-1 vertices in the remaining
 *     edge graph) so open chains trace as one loop instead of fragmenting.
 *   - At junctions with multiple unprocessed edges, picks the one whose outgoing
 *     direction best continues the incoming direction (same pinch-point heuristic
 *     the vertex-based version used).
 */
function buildBoundaryLoops(geometry, boundaryEdges, canonOf) {
  if (!canonOf) return { loops: [], openFlags: [] };

  const readPosition = avp.VertexEnumerator.makeReadPositionFunc(geometry);
  if (!readPosition) {
    console.warn('buildBoundaryLoops: geometry has no readable positions');
    return { loops: [], openFlags: [] };
  }

  const canonToOrig = new Map();
  const edges = []; // { c0, c1 }
  const vertexEdges = new Map(); // canon -> edge indices
  const seenEdgeKeys = new Set();

  for (const [a, b] of boundaryEdges) {
    const ca = canonOf[a];
    const cb = canonOf[b];
    if (ca === cb) continue; // degenerate after welding

    const key = ca < cb ? `${ca}_${cb}` : `${cb}_${ca}`;
    if (seenEdgeKeys.has(key)) continue;
    seenEdgeKeys.add(key);

    const eIdx = edges.length;
    edges.push({ c0: ca, c1: cb });

    if (!vertexEdges.has(ca)) vertexEdges.set(ca, []);
    if (!vertexEdges.has(cb)) vertexEdges.set(cb, []);
    vertexEdges.get(ca).push(eIdx);
    vertexEdges.get(cb).push(eIdx);

    if (!canonToOrig.has(ca)) canonToOrig.set(ca, a);
    if (!canonToOrig.has(cb)) canonToOrig.set(cb, b);
  }

  // Read a canonical vertex position into `out` (a Vector3). Returns `out` on
  // success, null if the canonical id has no mapping. Shared scratches in
  // callers keep the walker allocation-free on the hot path.
  const getPos = (canon, out) => {
    const origIdx = canonToOrig.get(canon);
    if (origIdx === undefined) return null;
    readPosition(origIdx, out);
    return out;
  };

  const processedEdges = new Set();

  const countUnprocessedAt = (canon) => {
    const list = vertexEdges.get(canon) || [];
    let n = 0;
    for (const i of list) if (!processedEdges.has(i)) n++;
    return n;
  };

  const _posJunction = new THREE.Vector3();
  const _posOther = new THREE.Vector3();

  const pickNextEdge = (atCanon, incomingDir) => {
    const incident = vertexEdges.get(atCanon) || [];
    let firstUnprocessed = -1;
    let multipleUnprocessed = false;
    for (const i of incident) {
      if (processedEdges.has(i)) continue;
      if (firstUnprocessed < 0) {
        firstUnprocessed = i;
      } else {
        multipleUnprocessed = true;
        break;
      }
    }
    if (firstUnprocessed < 0) return -1;
    if (!multipleUnprocessed || !incomingDir) return firstUnprocessed;

    // Multiple candidates: prefer the edge whose outgoing direction best continues incoming.
    if (!getPos(atCanon, _posJunction)) return firstUnprocessed;

    let best = firstUnprocessed;
    let bestDot = -Infinity;
    for (const i of incident) {
      if (processedEdges.has(i)) continue;
      const e = edges[i];
      const otherCanon = e.c0 === atCanon ? e.c1 : e.c0;
      if (!getPos(otherCanon, _posOther)) continue;
      _posOther.sub(_posJunction);
      const len = _posOther.length();
      if (len === 0) continue;
      const dot = incomingDir.dot(_posOther.divideScalar(len));
      if (dot > bestDot) {
        bestDot = dot;
        best = i;
      }
    }
    return best;
  };

  // Choose a starting edge. Prefer edges incident to an endpoint vertex (one
  // unprocessed edge) — that way open chains trace as one loop from their natural
  // endpoint instead of fragmenting. For closed rings (all degree-2) any edge works.
  const pickStart = () => {
    let endpointStart = null;
    let anyStart = null;
    for (let i = 0; i < edges.length; i++) {
      if (processedEdges.has(i)) continue;
      const e = edges[i];
      if (!anyStart) anyStart = { edgeIdx: i, startCanon: e.c0 };
      if (countUnprocessedAt(e.c0) === 1) {
        endpointStart = { edgeIdx: i, startCanon: e.c0 };
        break;
      }
      if (countUnprocessedAt(e.c1) === 1) {
        endpointStart = { edgeIdx: i, startCanon: e.c1 };
        break;
      }
    }
    return endpointStart || anyStart;
  };

  const rawLoops = []; // { canonSeq: number[], closed: boolean }
  const _p0 = new THREE.Vector3();
  const _p1 = new THREE.Vector3();

  while (processedEdges.size < edges.length) {
    const start = pickStart();
    if (!start) break;

    const { edgeIdx: startEdgeIdx, startCanon } = start;
    const startEdge = edges[startEdgeIdx];
    processedEdges.add(startEdgeIdx);

    const loopCanon = [startCanon, startEdge.c0 === startCanon ? startEdge.c1 : startEdge.c0];
    const vertexToPos = new Map();
    vertexToPos.set(loopCanon[0], 0);
    vertexToPos.set(loopCanon[1], 1);

    let closed = false;

    // Inner walk consumes one edge per iteration (processedEdges.add runs before any
    // branch), so edges.length is a hard upper bound. Guard against infinite loops
    // from a future regression in the consumed-edge invariant.
    for (let steps = 0; steps <= edges.length; steps++) {
      const lastCanon = loopCanon[loopCanon.length - 1];
      const prevCanon = loopCanon.length >= 2 ? loopCanon[loopCanon.length - 2] : -1;

      let incoming = null;
      if (prevCanon !== -1 && getPos(prevCanon, _p0) && getPos(lastCanon, _p1)) {
        _p1.sub(_p0);
        const len = _p1.length();
        if (len > 0) incoming = _p1.divideScalar(len);
      }

      const nextEdgeIdx = pickNextEdge(lastCanon, incoming);
      if (nextEdgeIdx < 0) break; // open terminate

      processedEdges.add(nextEdgeIdx);
      const ne = edges[nextEdgeIdx];
      const nextCanon = ne.c0 === lastCanon ? ne.c1 : ne.c0;

      const existingPos = vertexToPos.get(nextCanon);
      if (existingPos !== undefined) {
        if (existingPos === 0) {
          closed = true;
          break;
        }
        // Clip sub-loop [existingPos .. end] as a closed inner ring; continue
        // the main walk from the shared vertex.
        const subCanon = loopCanon.slice(existingPos);
        rawLoops.push({ canonSeq: subCanon, closed: true });
        for (let k = existingPos; k < loopCanon.length; k++) {
          vertexToPos.delete(loopCanon[k]);
        }
        loopCanon.length = existingPos;
        loopCanon.push(nextCanon);
        vertexToPos.set(nextCanon, loopCanon.length - 1);
        continue;
      }

      loopCanon.push(nextCanon);
      vertexToPos.set(nextCanon, loopCanon.length - 1);
    }

    rawLoops.push({ canonSeq: loopCanon, closed });
  }

  const loops = [];
  const openFlags = [];
  for (const r of rawLoops) {
    if (r.canonSeq.length < 2) continue;
    loops.push(r.canonSeq.map((c) => canonToOrig.get(c)));
    openFlags.push(!r.closed);
  }

  return { loops, openFlags };
}

/**
 * Get face boundaries based on a starting triangle and crease angle threshold
 * @param {THREE.BufferGeometry} geometry - The geometry of the mesh
 * @param {number} startTriangleIndex - The index of the starting triangle (face index * 3)
 * @param {number} creaseAngleRadians - The crease angle threshold in radians
 * @param {THREE.Matrix4} [matrixWorld] - The world transformation matrix, used to correct normals for non-uniform scaling
 * @returns {Object} An object containing boundaryEdges and isSurfaceCurved flag
 */
function getFaceBoundaries(geometry, startTriangleIndex, creaseAngleRadians = CREASE_ANGLE_RADIANS, matrixWorld) {
  if (!geometry || !geometry.ib) return { boundaryEdges: [], isSurfaceCurved: false };

  const canonOf = getCanonOf(geometry);
  if (!canonOf) return { boundaryEdges: [], isSurfaceCurved: false };

  const edgeMap = getEdgeMapWelded(geometry, canonOf);
  if (!edgeMap) return { boundaryEdges: [], isSurfaceCurved: false };

  const ib = geometry.ib;

  const readPosition = avp.VertexEnumerator.makeReadPositionFunc(geometry);

  const normals = geometry.attributes.normal?.array;
  const normalStride = 3;

  // Apply scale of the matrixWorld to prevent normal calculation inaccuracies
  // in case of degenerated unit-box transformations (see Snapper.js faceSnapping)
  const scale = matrixWorld ?
  new THREE.Vector3().setFromMatrixScale(matrixWorld) :
  new THREE.Vector3(1, 1, 1);
  // Guard against degenerate (near-zero) scale components that would produce NaN normals.
  if (scale.x < MIN_SCALE_COMPONENT || scale.y < MIN_SCALE_COMPONENT || scale.z < MIN_SCALE_COMPONENT) {
    scale.set(1, 1, 1);
  }

  // Returns a fresh THREE.Vector3 per call. Callers (BFS comparisons against `startNormal`)
  // hold on to the returned value across many subsequent calls, so we cannot reuse a
  // shared scratch here without aliasing bugs.
  function getTriangleNormal(triangleIdx) {
    const i0 = ib[triangleIdx];
    const i1 = ib[triangleIdx + 1];
    const i2 = ib[triangleIdx + 2];

    if (normals) {
      const n0 = new THREE.Vector3(
        normals[i0 * normalStride],
        normals[i0 * normalStride + 1],
        normals[i0 * normalStride + 2]
      );
      const n1 = new THREE.Vector3(
        normals[i1 * normalStride],
        normals[i1 * normalStride + 1],
        normals[i1 * normalStride + 2]
      );
      const n2 = new THREE.Vector3(
        normals[i2 * normalStride],
        normals[i2 * normalStride + 1],
        normals[i2 * normalStride + 2]
      );
      return n0.add(n1).add(n2).normalize();
    }

    const p0 = new THREE.Vector3();
    const p1 = new THREE.Vector3();
    const p2 = new THREE.Vector3();
    readPosition(i0, p0);
    readPosition(i1, p1);
    readPosition(i2, p2);
    return p1.sub(p0).cross(p2.sub(p0)).divide(scale).normalize();
  }

  const startNormal = getTriangleNormal(startTriangleIndex);

  // Find all triangles that are part of this face (similar normals).
  // BFS ensures triangles are discovered in approximate radial order.
  // This matters if a triangle count cap is reintroduced: nearby boundaries are visited first.
  const faceTriangles = new Set();
  const trianglesToCheck = [startTriangleIndex];
  let queueHead = 0;
  faceTriangles.add(startTriangleIndex);

  let isSurfaceCurved = false;

  while (queueHead < trianglesToCheck.length) {
    const currentTriIdx = trianglesToCheck[queueHead++];
    const currentNormal = getTriangleNormal(currentTriIdx);

    // Get the three edges of this triangle
    const v0 = ib[currentTriIdx];
    const v1 = ib[currentTriIdx + 1];
    const v2 = ib[currentTriIdx + 2];

    const edges = [
    [v0, v1],
    [v1, v2],
    [v2, v0]];


    // Check each edge for adjacent triangles
    for (const edge of edges) {
      const edgeKey = makeEdgeKey(canonOf, edge[0], edge[1]);
      const adjacentTriangles = edgeMap.get(edgeKey) || [];

      for (const adjTriIdx of adjacentTriangles) {
        if (adjTriIdx === currentTriIdx || faceTriangles.has(adjTriIdx)) {
          continue;
        }

        const adjNormal = getTriangleNormal(adjTriIdx);
        const angle = Math.acos(Math.max(-1, Math.min(1, currentNormal.dot(adjNormal))));

        // If angle is less than crease angle, include this triangle
        if (angle < creaseAngleRadians) {
          faceTriangles.add(adjTriIdx);
          trianglesToCheck.push(adjTriIdx);

          // Also check if this new triangle deviates from the original normal
          if (!isSurfaceCurved) {
            const angleToStart = Math.acos(Math.max(-1, Math.min(1, startNormal.dot(adjNormal))));
            if (angleToStart > ANGLE_PRECISION) {
              isSurfaceCurved = true;
            }
          }
        }
      }
    }
  }

  // Deduplicate by canonical key — non-manifold edges touch multiple face-set triangles
  // and would otherwise produce duplicate adj entries causing false pinch-points.
  const seenBoundaryKeys = new Set();
  const boundaryEdges = [];

  for (let qi = 0; qi < trianglesToCheck.length; qi++) {
    const triangleIdx = trianglesToCheck[qi];
    const v0 = ib[triangleIdx];
    const v1 = ib[triangleIdx + 1];
    const v2 = ib[triangleIdx + 2];

    const edges = [
    [v0, v1],
    [v1, v2],
    [v2, v0]];


    for (const edge of edges) {
      const edgeKey = makeEdgeKey(canonOf, edge[0], edge[1]);
      const adjacentTriangles = edgeMap.get(edgeKey) || [];

      // Boundary = mesh border or at least one neighbour outside the face set.
      const isBoundary = adjacentTriangles.length === 1 ||
      adjacentTriangles.some((t) => t !== triangleIdx && !faceTriangles.has(t));

      if (isBoundary && !seenBoundaryKeys.has(edgeKey)) {
        seenBoundaryKeys.add(edgeKey);
        boundaryEdges.push(edge);
      }
    }
  }

  return { boundaryEdges, isSurfaceCurved };
}

/**
 * Convert boundary edges to world coordinate vertices
 * @param {number[][]} orderedBoundaryEdges - Array of chains, each chain is an array of vertex indices
 * @param {THREE.BufferGeometry} geometry - The original geometry
 * @param {THREE.Matrix4} worldMatrix - The world transformation matrix
 * @returns {THREE.Vector3[][]} Array of chains, each chain is an array of world coordinate vertices
 */
function convertBoundaryEdgesToWorldVertices(orderedBoundaryEdges, geometry, worldMatrix) {
  const readPosition = avp.VertexEnumerator.makeReadPositionFunc(geometry);
  if (!readPosition) {
    console.warn('Invalid geometry data for vertex extraction');
    return [];
  }

  const worldVertexChains = [];

  // Process each chain of boundary edges
  for (const chain of orderedBoundaryEdges) {
    const worldVertices = [];

    // Convert each vertex index in the chain to world coordinates
    for (let i = 0; i < chain.length; i++) {
      const worldVertex = new THREE.Vector3();
      readPosition(chain[i], worldVertex);

      if (worldMatrix) {
        worldVertex.applyMatrix4(worldMatrix);
      }

      worldVertices.push(worldVertex);
    }

    worldVertexChains.push(worldVertices);
  }

  return worldVertexChains;
}

/**
 * Filter collinear points, remove points that are close to the line formed by the previous and next points.
 * @param {THREE.Vector3[]} points - The points to filter
 * @param {boolean} [isOpen=false] - Whether the point sequence is an open chain (endpoints always kept)
 * @returns {THREE.Vector3[]} The filtered points
 */
const filterCollinearPoints = (points, isOpen = false) => {
  if (!points || points.length === 0) {
    return points;
  }

  const filteredPoints = [];
  const n = points.length;
  for (let i = 0; i < n; i++) {
    if (isOpen && (i === 0 || i === n - 1)) {// keep endpoints of open chains
      filteredPoints.push(points[i]);
      continue;
    }
    const previousPoint = points[(i - 1 + n) % n];
    const currentPoint = points[i];
    const nextPoint = points[(i + 1) % n];
    const previousVector = previousPoint.clone().sub(currentPoint);
    const nextVector = currentPoint.clone().sub(nextPoint);
    const crossProduct = new THREE.Vector3().crossVectors(previousVector, nextVector);
    if (crossProduct.length() > EPSILON) {
      filteredPoints.push(currentPoint);
    }
  }
  if (filteredPoints.length < 3) {
    return points;
  }

  return filteredPoints;
};

/**
 * Detects if a loop has crease angles indicating a compound shape.
 * Returns an array of vertex loops - one loop for simple shapes, multiple loops for compound shapes.
 * @param {THREE.Vector3[]} vertices - The vertices of the loop
 * @returns {THREE.Vector3[][]} Array of vertex loops (single element for simple shapes)
 */
function detectAndSplitCompoundLoop(vertices) {
  if (vertices.length < MIN_CIRCLE_EDGES) {
    return [vertices]; // Too few vertices to be compound
  }
  // Check if this loop has significant crease angles (direction changes)
  const creaseIndices = [];

  for (let i = 0; i < vertices.length; i++) {
    const prev = vertices[(i - 1 + vertices.length) % vertices.length];
    const curr = vertices[i];
    const next = vertices[(i + 1) % vertices.length];

    // Calculate vectors
    const v1 = curr.clone().sub(prev).normalize();
    const v2 = next.clone().sub(curr).normalize();

    // Calculate angle between vectors
    const angle = v1.angleTo(v2);
    const interiorAngle = Math.PI - angle;

    // If the interior angle is outside curve continuity tolerance,
    // we treat it as a crease indicating a compound shape.
    if (interiorAngle < MIN_CURVE_INTERIOR_ANGLE || interiorAngle > MAX_CURVE_INTERIOR_ANGLE) {
      creaseIndices.push(i);
    }
  }

  // If no creases found, it's a simple shape
  if (creaseIndices.length === 0) {
    return [vertices];
  }

  // Split the loop at crease points into separate vertex segments
  const segments = [];
  for (let i = 0; i < creaseIndices.length; i++) {
    const startIdx = creaseIndices[i];
    const endIdx = creaseIndices[(i + 1) % creaseIndices.length];

    const segment = [];
    let idx = startIdx;
    while (idx !== endIdx) {
      segment.push(vertices[idx]);
      idx = (idx + 1) % vertices.length;
    }
    segment.push(vertices[endIdx]); // Include the end vertex

    if (segment.length >= 2) {
      segments.push(segment);
    }
  }

  return segments.length > 0 ? segments : [vertices];
}

/**
 * Extracts surface geometry data from a pick result
 * @param {Object} pick - The pick result containing surface information
 * @param {Object} viewer - The viewer instance
 * @returns {Object|null} Surface data with geometry and normal information
 */
function extractSurfaceData(pick, viewer) {
  if (typeof pick.fragId !== 'number' || !pick.originalFace || typeof pick.originalFace.faceIndex !== 'number') {
    return null;
  }

  const face = pick.geomFace;
  const faceNormal = pick.faceNormal;
  const intersectPoint = pick.intersectPoint;
  const fragId = pick.fragId;
  const modelId = pick.modelId;

  if (!face?.vertices || face.vertices.length < 3) {
    return null;
  }

  const fragmentList = viewer.model.getFragmentList();
  const fragmentGeometry = fragmentList.getGeometry(fragId);

  // Bail without a result on missing geometry or non-indexed meshes; the rest of the
  // pipeline assumes an index buffer.
  if (!fragmentGeometry || !fragmentGeometry.ib) {
    return null;
  }

  // Above the cap we silently skip — the surface tool is interactive and the BFS+weld
  // cost on huge meshes (e.g. monolithic Revit shells) makes hover unresponsive.
  // Known UX gap: the user gets no feedback that the click was ignored.
  if (fragmentGeometry.ib.length > MAX_SURFACE_INDEX_BUFFER) {
    return null;
  }

  const dstMatrix = new THREE.Matrix4();
  fragmentList.getWorldMatrix(fragId, dstMatrix);

  let orderedBoundaryEdges = [];
  let openFlags = [];
  let filteredBoundaryVerticesWorld = [];
  let isSurfaceCurved = false;

  const startTriangleIndex = pick.originalFace.faceIndex * 3;
  const { boundaryEdges, isSurfaceCurved: curved } =
  getFaceBoundaries(fragmentGeometry, startTriangleIndex, CREASE_ANGLE_RADIANS, dstMatrix);

  isSurfaceCurved = curved;
  const canonOf = getCanonOf(fragmentGeometry);
  const result = buildBoundaryLoops(fragmentGeometry, boundaryEdges, canonOf);
  orderedBoundaryEdges = result.loops;
  openFlags = result.openFlags;

  // Compact rolling hash of sorted boundary indices — avoids unbounded string length.
  const allVertexIndices = orderedBoundaryEdges.flat().sort((a, b) => a - b);
  let vertexHash = allVertexIndices.length;
  for (let i = 0; i < allVertexIndices.length; i++) {
    vertexHash = Math.imul(vertexHash, 31) + allVertexIndices[i] >>> 0;
  }
  const surfaceId = `${modelId}-${fragId}-${vertexHash.toString(36)}`;

  const boundaryVerticesWorld = convertBoundaryEdgesToWorldVertices(orderedBoundaryEdges, fragmentGeometry, dstMatrix);

  // Curved surfaces have no closed-form area from the boundary alone; we report null and
  // let callers (e.g. UI labels) decide how to communicate that.
  const closedLoopAreas = [];
  let largestArea = null;
  for (let i = 0; i < boundaryVerticesWorld.length; i++) {
    const isOpen = openFlags[i];
    const filteredLoopVertices = filterCollinearPoints(boundaryVerticesWorld[i], isOpen);
    filteredBoundaryVerticesWorld.push(filteredLoopVertices);
    if (!isSurfaceCurved && !isOpen) {// open loops have no well-defined area
      const area = calculateSurfaceArea(filteredLoopVertices);
      closedLoopAreas.push(area);
    }
  }
  // Outer-vs-inner loop heuristic: assume the largest closed loop is the outer boundary
  // and the rest are holes, then subtract holes from the outer area. Known limitations:
  //   - calculateSurfaceArea is unsigned (vector area magnitude), so we cannot tell hole
  //     orientation from CCW/CW. Faces with multiple disjoint outer loops in the same plane
  //     would over-subtract.
  //   - Holes that geometrically overlap each other would also over-subtract.
  // Acceptable for typical Revit/CAD faces (one outer loop + 0..N holes).
  if (!isSurfaceCurved) {
    closedLoopAreas.sort((a, b) => b - a);
    largestArea = closedLoopAreas[0];
    for (let i = 1; i < closedLoopAreas.length; i++) {
      largestArea -= closedLoopAreas[i];
    }
  }

  const detectedShapes = [];
  for (let i = 0; i < filteredBoundaryVerticesWorld.length; i++) {
    const vertices = filteredBoundaryVerticesWorld[i];
    const isOpen = openFlags[i];

    if (isOpen) {
      // Skip detectAndSplitCompoundLoop — its wrap-around indexing treats the virtual
      // closing edge as real, producing spurious lines on open chains.
      for (const shape of detectShape(vertices, false)) {
        detectedShapes.push(shape);
      }
    } else {
      const groupsOfVertexSegments = detectAndSplitCompoundLoop(vertices);
      const isClosedLoop = groupsOfVertexSegments.length === 1;
      for (const segment of groupsOfVertexSegments) {
        for (const shape of detectShape(segment, isClosedLoop)) {
          detectedShapes.push(shape);
        }
      }
    }
  }

  // Two concentric circles → cylinder/cone face: synthesize a HEIGHT shape so the UI
  // can label the axial distance between the caps.
  if (detectedShapes.length === 2 &&
  detectedShapes[0].type === SHAPE_TYPES.CIRCLE &&
  detectedShapes[1].type === SHAPE_TYPES.CIRCLE) {
    const centroid0 = detectedShapes[0].centroid;
    const centroid1 = detectedShapes[1].centroid;
    detectedShapes.push({
      type: SHAPE_TYPES.HEIGHT,
      centroid: getCentroid([centroid0, centroid1]),
      vertices: [centroid0, centroid1],
      perimeter: centroid0.distanceTo(centroid1),
      start: centroid0,
      end: centroid1,
      isClosed: false
    });
  }

  return {
    surfaceId,
    vertices: filteredBoundaryVerticesWorld,
    openLoops: openFlags,
    normal: faceNormal,
    intersectPoint,
    area: isSurfaceCurved ? null : largestArea,
    boundaryEdges: orderedBoundaryEdges,
    boundaryVerticesWorld: filteredBoundaryVerticesWorld,
    detectedShapes,
    isSurfaceCurved
  };
}

/**
 * Detects a shape from a list of vertices. The supported shapes are: circle, ellipse, circular arc, simple curve, rectangle.
 * @param {THREE.Vector3[]} vertices - The vertices of the shape
 * @param {Object} [options] - Detection options
 * @param {boolean} [isClosedLoop] - Whether the vertices represent a closed loop
 * @returns {Object} The detected shape
 */
function detectShape(vertices, isClosedLoop) {
  if (vertices?.length < 2) {
    return [];
  }
  // if the shape has only 2 vertices, it is a line.
  if (vertices.length < 3) {
    return [{ type: SHAPE_TYPES.LINE, vertices, isClosed: false }];
  }

  if (isClosedLoop) {
    const circle = detectCircleFromLoop(vertices);
    if (circle) {
      circle.isClosed = true;
      return [circle];
    }
    const ellipse = detectEllipseFromLoop(vertices);
    if (ellipse) {
      ellipse.isClosed = true;
      return [ellipse];
    }
    // Not a circle/ellipse: decompose into arc/line primitives. `detectCurvesAndLines`
    // expects an open polyline, so close a loop by repeating the first vertex at the end.
    const chain = [...vertices, vertices[0]];
    const curvesAndLines = detectCurvesAndLines(chain);
    if (curvesAndLines.length > 0) {
      // All-line closed chains fold back to a single POLYGON: a flat rectangular
      // face should render as one shape with a perimeter label, not N per-edge
      // length labels. Any arc/curve in the decomposition breaks the fold — those
      // genuinely have per-segment geometry worth surfacing individually.
      const allLines = curvesAndLines.every((s) => s.type === SHAPE_TYPES.LINE);
      if (allLines) {
        return [{ type: SHAPE_TYPES.POLYGON, vertices, isClosed: true }];
      }
      curvesAndLines.forEach((s) => {s.isClosed = false;});
      return curvesAndLines;
    }
  } else {
    const curvesAndLines = detectCurvesAndLines(vertices);
    if (curvesAndLines.length > 0) {
      curvesAndLines.forEach((s) => {s.isClosed = false;});
      return curvesAndLines;
    }
  }

  // if no other shape is detected, consider it a polygon.
  return [{ type: SHAPE_TYPES.POLYGON, vertices, isClosed: isClosedLoop }];
}